import { useState, useEffect } from "react";
import { rtdb } from "../../services/firebase";
import { ChatListing } from "../../types/db/chat";
import { WithID } from "../../types/db";
import { ChatListingService } from "../../types/services/chat";

/** Listing info is the info of a listing with the listing id */
type ListingInfo = WithID<ChatListing>;

/**
 * Hook which takes care of fetching and updating the listing info of a chat
 * @param listingID The listing ID
 * @returns the listing info
 */
export const useChatListing = (listingId: string): {
    /** The listing info */
    listing: ListingInfo | null;
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
} => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [listing, setListing] = useState<ListingInfo | null>(null);

    useEffect(() => {
        if (!listingId) {
            //if no listingId no need to go farther
            setIsLoading(false);
            return;
        }
        //update the current listing with the listing info from BE
        setIsLoading(true);
        //subscribe to any child/info of that listing
        const fetchListingAbout = (new ChatListingService(rtdb)).getEntityRef(listingId);

        fetchListingAbout
            .on("value", (snapshot) => {
                const listingAbout: ChatListing | null = snapshot.val();
                if (!snapshot.exists() || !listingAbout) {
                    //can happen if doesn't exist
                    setError('Listing not found');
                    setListing(null);
                    return;
                }
                setListing({ ...listingAbout, id: listingId });
                setIsLoading(false);
            });

        return () => {
            //unsubscribe to all listener for the listing
            fetchListingAbout.off();
            //reset the listing info
            setListing(null);
        };
    }, [listingId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the listing doesn't exist
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    return {
        listing,
        isLoading,
        error,
    }
}